// It contains data for Location Search component
import { createAction, createReducer } from '@reduxjs/toolkit';
import * as log from 'loglevel';

import UISettings from '../settings/UISettings';
import {
  LOCALSTORAGE_LOCATION_HISTORY_ANON,
  LOCALSTORAGE_LOCATION_HISTORY_SESSION,
  storeResolvedLocationsToLocalStorage
} from './helpers/localStorageHelpers';
import {
  getGeolocation,
  resolveLocationByGoogleAPI,
  searchSuggestions
} from '../UIComponents/LocationSearch/helpers/locationSearchHelpers';
import { getActiveListingPage, getActiveListingPageProductIds } from './selectors';
import { searchDeliveriesForLocation } from './listingPagePages.reduck';

const GOOGLE_PLACES_URL = 'https://maps.googleapis.com/maps/api/js?libraries=places';
const GOOGLE_CLIENT_API_KEY = 'client=gme-dsgretaillimited2';

// 1. Initial state
const initialState = {
  // it determines if location search has been initialized
  locationSearchInitialized: false,

  // it contains previously locations for which user searched channels availabilities before
  previouslyResolvedLocations: [],

  // if geolocation button should be shown or not
  geolocationEnabled: false,

  // if true, the gelocation gathering is in process
  searchingGeolocation: false,

  // Contains string message when searching geolocation has failed
  geolocationSearchFailedErrorMessage: '',

  resolvingLocation: false
};

// 2. Basic actions creators
const setLocationSearchInitialized = createAction('listingPage/locationSearchSetup/locationSearchInitialized');
const setPreviouslyResolvedLocations = createAction('listingPage/locationSearchSetup/setPreviouslyResolvedLocations');
const addPreviouslyResolvedLocation = createAction('listingPage/locationSearchSetup/addPreviouslyResolvedLocation');
const setGeolocationEnabled = createAction('listingPage/locationSearchSetup/setGeolocationEnabled');
const setSearchingGeolocation = createAction('listingPage/locationSearchSetup/setSearchingGeolocation');
export const setGeolocationSearchFailedErrorMessage =
  createAction('listingPage/locationSearchSetup/setGeolocationSearchFailedErrorMessage');
const setResolvingLocation = createAction('listingPage/locationSearchSetup/setResolvingLocation');


// 3. Complex actions which call basic actions above

const setPreviouslyResolvedLocationsFromLocalStorage = () => (dispatch, getState) => {

  // It's called only in browser and if localStorage is supported in browser
  if (typeof window !== 'undefined' && window.localStorage !== undefined) {
    const isLoggedInUser = typeof getState().configuration.user === 'object' && getState().configuration.user.isLoggedIn;
    const localStorageKey = isLoggedInUser ? LOCALSTORAGE_LOCATION_HISTORY_SESSION : LOCALSTORAGE_LOCATION_HISTORY_ANON;

    try {
      const locationHistoryString = window.localStorage.getItem(localStorageKey);
      const locationHistoryArray = JSON.parse(locationHistoryString);

      if (Array.isArray(locationHistoryArray)) {
        dispatch(setPreviouslyResolvedLocations(locationHistoryArray));
      }

    } catch (e) {
      log.warn('It wasn\'t possible to gather user\'s previous used location from local storage');
    }
  }
};

// This function does searching for channels availabilites after load/reload a page in browser
// if user previously selected a location using Location Search component and if autostock checking is turned on in UCMS
const doAutoStockCheckAfterPageLoad = () => async (dispatch, getState) => {

  // autostock check is done only if user's postcode coming from groupcall and products request is not pending
  if (getState().configuration.userPostcode && !getState().listingPage.commonData.xhrStatus.pendingProductsRequest) {
    log.info(`Auto stock checker for postcode: ${getState().configuration.userPostcode} in progress`);

    const locationDetails = await searchSuggestions(getState().configuration.userPostcode);

    if (locationDetails.length > 0) {
      dispatch(resolveLocationAndSearchDeliveries(
        locationDetails[0],
        getActiveListingPageProductIds(getState()),
        getActiveListingPage(getState()).pageUrlSuffix
      )
      );
    } else {
      log.warn(`Auto stock checker for postcode: ${getState().configuration.userPostcode} FAILED`);
    }
  }
};

// it resolves location using Google API
const resolveLocation = (locationDescriptor) => async (dispatch, getState) => {
  try {
    dispatch(setResolvingLocation(true));

    const resolvedLocationDescriptor = await resolveLocationByGoogleAPI(locationDescriptor);

    if (getState().listingPage.locationSearchSetup.previouslyResolvedLocations
      .findIndex((location) => location.id === resolvedLocationDescriptor.id) === -1) {
      // if location was searched for the first time then it's added to previously used locations
      dispatch(addPreviouslyResolvedLocation(resolvedLocationDescriptor));
      // store previously used locations to local storage
      storeResolvedLocationsToLocalStorage(
        getState().listingPage.locationSearchSetup.previouslyResolvedLocations,
        typeof getState().configuration.user === 'object' && getState().configuration.user.isLoggedIn
      );
    }

    dispatch(setResolvingLocation(false));
    return {
      ok: true,
      resolvedLocationDescriptor
    };
  } catch (err) {
    dispatch(setResolvingLocation(false));

    return {
      ok: false,
      customErrorMessage: 'Error occured during resolving location',
      errorObject: err,
      errorCode: 500
    };
  }
};

export const initLocationSearch = () => (dispatch) => {
  log.info('[LOCATION SEARCH] init starts');

  // if geolocation is enabled in settings, it's set enabled
  if (UISettings.get('component.mcd.geolocation.enabled') !== false) {
    dispatch(setGeolocationEnabled(true));
  }

  // init of Google API
  window.GoogleAPIReady = function GoogleAPIReady() {
    // get previously resolved locations from local storage
    dispatch(setPreviouslyResolvedLocationsFromLocalStorage());

    // set that location search is initialized
    dispatch(setLocationSearchInitialized(true));
    log.info('[LOCATION SEARCH] init finished successfully');

    // do auto stock check if enabled
    if (UISettings.get('component.mcd.stockchecker.auto') !== false) {
      dispatch(doAutoStockCheckAfterPageLoad());
    }
  };

  window.gm_authFailure = (() => {
    log.error('[LOCATION SEARCH] init failed');
    log.error("It wasn't possible to init google API so locations search is turned off");
  });

  const script = document.createElement('SCRIPT');
  script.setAttribute('type', 'text/javascript');
  script.setAttribute('async', true);
  script.setAttribute('defer', true);
  script.src = `${GOOGLE_PLACES_URL}&callback=GoogleAPIReady&${GOOGLE_CLIENT_API_KEY}`;
  const head = document.head || document.getElementsByTagName('head')[0];
  head.appendChild(script);
};

export const searchDeliveriesForGeoLocation = (productIds, listingPageUrl) => async (dispatch) => {
  let geoLocationResolverObject;

  try {
    dispatch(setSearchingGeolocation(true));
    geoLocationResolverObject = await getGeolocation();
    dispatch(setSearchingGeolocation(false));

  } catch (e) {
    dispatch(setSearchingGeolocation(false));
    dispatch(setGeolocationSearchFailedErrorMessage('Unknown error during gathering geolocation'));
  }

  if (geoLocationResolverObject.ok) {
    // geolocation gathered correctly
    dispatch(resolveLocationAndSearchDeliveries(geoLocationResolverObject.location, productIds, listingPageUrl));
  } else {
    dispatch(setGeolocationSearchFailedErrorMessage(geoLocationResolverObject.errorMessage));
  }
};

export const resolveLocationAndSearchDeliveries = (locationDescriptor, productIds, listingPageUrl) => async (dispatch) => {
  const resolvingLocationResult = await dispatch(resolveLocation(locationDescriptor));

  if (!resolvingLocationResult.ok) {
    log.error(resolvingLocationResult.customErrorMessage);

    return;
  }

  await dispatch(searchDeliveriesForLocation(resolvingLocationResult.resolvedLocationDescriptor, productIds, listingPageUrl));
};


// 4. Reducer
/* eslint-disable no-param-reassign */
export default createReducer(initialState, {
  [setLocationSearchInitialized]: (stateDraft, action) => {
    stateDraft.locationSearchInitialized = action.payload;
  },
  [setPreviouslyResolvedLocations]: (stateDraft, action) => {
    stateDraft.previouslyResolvedLocations = action.payload;
  },
  [addPreviouslyResolvedLocation]: (stateDraft, action) => {
    stateDraft.previouslyResolvedLocations.unshift(action.payload);
  },
  [setGeolocationEnabled]: (stateDraft, action) => {
    stateDraft.geolocationEnabled = action.payload;
  },
  [setSearchingGeolocation]: (stateDraft, action) => {
    stateDraft.searchingGeolocation = action.payload;
  },
  [setGeolocationSearchFailedErrorMessage]: (stateDraft, action) => {
    stateDraft.geolocationSearchFailedErrorMessage = action.payload;
  },
  [setResolvingLocation]: (stateDraft, action) => {
    stateDraft.resolvingLocation = action.payload;
  }
});
